<script setup lang="ts">
// NPM
import { ref, computed, reactive } from 'vue';
import { useMq } from 'vue3-mq';

// Components
import {
  BaseCard,
  BaseEmptyState,
  Modal,
  BaseButton,
  BaseBadge,
  FieldMultiFileUploader,
  FieldInput,
  FieldTextarea,
  LinkedText,
} from '@magma-app/magma-lapilli';

// API
import { useChallenges } from '@/api/challenges/use-challenges';
import { useAddProof } from '@/api/challenges/use-add-proof';
import { useActionFileUpload } from '@/composables/useUgcFileUpload';
import { useActionDeepLink } from '@/composables/useActionDeepLink';
import type { UserDashboardGetAllChallengesResponseType } from '@magma-app/magma-types/user-dashboard/challenge';

// Plugins
const mq = useMq();

type OngoingChallenge = UserDashboardGetAllChallengesResponseType['ongoing'][number];

const { data: challenges, isLoading, refetch: refetchChallenges } = useChallenges();
const { mutateAsync: addProof, isPending: isSubmittingProof } = useAddProof();

const MAX_ACTION_FILE_SIZE = 300 * 1000 * 1000;
const ACTION_FILE_ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.avif,.heic,.mp4,.mov,.webm,.mkv,.m4v,.pdf';

const getBaseCardStatus = (status: OngoingChallenge['status']) => {
  if (status === 'interested') {
    return 'challengeActionNeeded';
  }

  if (status === 'toValidate') {
    return 'challengeToValidate';
  }

  return null;
};

const regex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w-./?%&=]*)?$/;

const testLink = computed(() => {
  return regex.test(proof.link);
});

const modal = ref<number | null>(null);
const currentModalStep = ref<'start' | 'add' | 'end' | 'waiting'>('start');
const selectedUserChallengeId = ref(0);
const proofFiles = ref<File[]>([]);
const fileUpload = useActionFileUpload(proofFiles, 'challenge', selectedUserChallengeId);
const submissionError = ref(false);
const proof = reactive({
  text: '',
  link: '',
});

const getChallengeImgUrl = (challenge: { pictureUrl?: string | null }) =>
  challenge.pictureUrl
    ? challenge.pictureUrl
    : mq.lgPlus
      ? '/img/challenge_lg.png'
      : '/img/challenge.png';

const resetProof = () => {
  proof.text = '';
  proof.link = '';
  proofFiles.value = [];
  submissionError.value = false;
  fileUpload.clearAll();
};

const stripHttpProtocol = (value?: string | null) =>
  value?.replace(/^https?:\/\//i, '') || '';

const getProofFiles = (challenge: any) => {
  if (challenge.proofFiles?.length) {
    return challenge.proofFiles;
  }

  if (!challenge.photoProofUrl) {
    return [];
  }

  return [{
    id: -(challenge.userChallengeId || 0),
    url: challenge.photoProofUrl,
    originalFilename: 'proof-file',
    filename: 'proof-file',
    mimeType: 'application/octet-stream',
    size: 0,
  }];
};

const refreshProofUrlsIfNeeded = (challenge: any) => {
  if (challenge.status === 'toValidate' && getProofFiles(challenge).length) {
    void refetchChallenges();
  }
};

const openChallengeModal = (challenge: OngoingChallenge) => {
  modal.value = challenge.challengeId;
  selectedUserChallengeId.value = challenge.userChallengeId || 0;
  currentModalStep.value =
    challenge.status === 'interested' ? 'start' : 'waiting';

  if (challenge.status === 'toValidate') {
    refreshProofUrlsIfNeeded(challenge);
  }
};

const closeChallengeModal = () => {
  modal.value = null;
  currentModalStep.value = 'start';
  resetProof();
  selectedUserChallengeId.value = 0;
};

// Opens one challenge's modal directly from a ?challengeId= deep link.
useActionDeepLink<OngoingChallenge>({
  type: 'challenge',
  bucket: 'ongoing',
  onOpen: (challenge) => openChallengeModal(challenge),
});

const submitProof = async (challenge: any) => {
  submissionError.value = false;

  try {
    await addProof({
      userChallengeId: challenge.userChallengeId || 0,
      textProof: proof.text.trim() || undefined,
      linkProofUrl: proof.link.trim() || undefined,
      fileKeys: fileUpload.completedFiles.value.length
        ? fileUpload.completedFiles.value
        : undefined,
    });
    currentModalStep.value = 'end';
  } catch {
    submissionError.value = true;
  }
};
</script>

<template>
  <div
    v-if="!isLoading"
    class="block h-full flex-1 px-4 md:px-8"
    :class="{
      'content-start lg:flex lg:flex-wrap': challenges?.ongoing.length
    }"
  >
    <template v-if="challenges?.ongoing.length">
      <Modal
        v-for="challenge in challenges?.ongoing"
        :key="challenge.userChallengeId"
        :open="modal === challenge.challengeId"
        content-class="!max-h-[calc(100dvh-2rem)] max-w-[90dvw] !overflow-y-auto overscroll-contain sm:max-w-[480px]"
        @open-focus="(e) => e.preventDefault()"
        @open-change="
          (open) =>
            open ? openChallengeModal(challenge) : closeChallengeModal()
        "
      >
        <template #trigger>
          <BaseCard
            aria-hidden="true"
            class="mb-2 lg:mb-6 lg:mr-6"
            :title="challenge.name"
            :description="challenge.description"
            :img-url="getChallengeImgUrl(challenge)"
            :points="challenge.points"
            :badge-status="{ points: challenge.points, type: 'challenge' }"
            :display-points="!challenge.pictureUrl"
            :status="getBaseCardStatus(challenge.status)"
          />
        </template>
        <template #content>
          <div v-if="currentModalStep === 'start'" class="min-w-0 flex flex-col gap-6">
            <div class="flex flex-col gap-4">
              <img
                :src="getChallengeImgUrl(challenge)"
                :alt="challenge.name"
                class="h-[200px] w-full rounded-[5px] object-cover"
              />
              <div class="flex flex-col gap-1">
                <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
                  {{ challenge.name }}
                </h3>
                <BaseBadge
                  size="md"
                  :text="challenge.points"
                  icon-type="icon"
                  icon-name="star"
                />
              </div>
              <div
                class="
                  rich-text-content max-h-[40dvh] min-w-0 break-words
                  overflow-y-auto text-mgm-txt-sm text-foreground-default
                  lg:max-h-none
                "
                v-html="challenge.description"
              ></div>
            </div>
            <BaseButton
              size="lg"
              class="w-full"
              @click="currentModalStep = 'add'"
              >{{ $t('challenges.ongoing.add.cta') }}</BaseButton
            >
          </div>
          <div v-if="currentModalStep === 'add'" class="min-w-0 flex flex-col gap-6">
            <div
              class="w-12 h-12 bg-[#F1F5F9] rounded-full border-8 border-[#F8FAFC] flex items-center justify-center"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                  stroke="#5F6C85"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="flex flex-col gap-1">
              <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
                {{ $t('challenges.ongoing.add.title') }}
              </h3>

              <p class="break-words text-mgm-txt-sm text-foreground-default whitespace-pre-line">
                <LinkedText :text="challenge.descriptionProof" />
              </p>
            </div>

            <div class="flex flex-col gap-5">
              <FieldTextarea
                v-if="challenge.needTextProof"
                v-model="proof.text"
                :label="$t('challenges.ongoing.add.comment')"
              />

              <FieldMultiFileUploader
                v-if="challenge.needPhotoProof"
                :id="`challenge-file-${challenge.userChallengeId}`"
                v-model="proofFiles"
                :label="$t('challenges.ongoing.add.photo')"
                :upload-states="fileUpload.uploadStates.value"
                :max-file-size="MAX_ACTION_FILE_SIZE"
                required
                :accept="ACTION_FILE_ACCEPT"
                @file-remove="fileUpload.onFileRemoved"
                @file-rename="fileUpload.renameFile"
                @file-retry="fileUpload.retryFile"
              />

              <FieldInput
                v-if="challenge.needLinkProof"
                :model-value="proof.link"
                role="link"
                required
                :label="$t('challenges.ongoing.add.link')"
                @update:model-value="
                  (e) => {
                    proof.link = stripHttpProtocol(e);
                  }
                "
              />
              <p
                v-if="submissionError"
                class="text-mgm-txt-xs text-foreground-danger-default"
              >
                {{ $t('generic.error') }}
              </p>
            </div>

            <div class="flex gap-2">
              <BaseButton
                size="lg"
                class="flex-1"
                state="scdr"
                icon="chevron-left"
                icon-position="left"
                :disabled="isSubmittingProof || fileUpload.isUploading.value"
                @click="currentModalStep = 'start'"
                >{{ $t('challenges.ongoing.add.back') }}</BaseButton
              >
              <BaseButton
                size="lg"
                class="flex-1"
                :loading="isSubmittingProof"
                :disabled="
                  (challenge.needTextProof && proof.text === '') ||
                  (challenge.needPhotoProof && !fileUpload.allUploaded.value) ||
                  (challenge.needLinkProof && proof.link === '') ||
                  (challenge.needLinkProof && !testLink) ||
                  fileUpload.isUploading.value ||
                  fileUpload.hasErrors.value ||
                  isSubmittingProof
                "
                @click="submitProof(challenge)"
                >{{ $t('challenges.ongoing.add.cta') }}</BaseButton
              >
            </div>
          </div>
          <div v-if="currentModalStep === 'end'" class="min-w-0">
            <div
              class="w-12 h-12 bg-[#D1FADF] rounded-full border-8 border-[#ECFDF3] flex items-center justify-center mb-5"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999M22 3.99999L12 14.01L9.00001 11.01"
                  stroke="#03874C"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
              {{ $t('challenges.ongoing.end.title') }}
            </h3>
            <ul
              class="list-disc pl-0 text-mgm-txt-sm text-foreground-default my-4"
            >
              <p>
                {{
                  $t('challenges.ongoing.end.content', {
                    email: challenge.contactAdminEmail
                  })
                }}
              </p>
            </ul>
            <BaseButton
              size="lg"
              class="w-full"
              @click="
                () => {
                  currentModalStep = 'start';
                  modal = null;
                }
              "
              >{{ $t('generic.close') }}</BaseButton
            >
          </div>
          <div v-if="currentModalStep === 'waiting'" class="min-w-0">
            <img
              :src="getChallengeImgUrl(challenge)"
              :alt="challenge.name"
              class="h-[200px] w-full rounded-[5px] object-cover mb-5"
            />
            <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
              {{ challenge.name }}
            </h3>
            <BaseBadge
              class="mt-2"
              size="md"
              :text="challenge.points"
              icon-type="icon"
              icon-name="star"
            />
            <div
              class="
                rich-text-content my-5 max-h-[40dvh] min-w-0 break-words
                overflow-y-auto text-mgm-txt-sm text-foreground-default
                lg:max-h-none
              "
              v-html="challenge.description"
            ></div>
            <div
              v-if="challenge.textProof || challenge.linkProofUrl || getProofFiles(challenge).length"
              class="flex flex-col gap-3 mb-5"
            >
              <FieldTextarea
                v-if="challenge.textProof"
                :model-value="challenge.textProof"
                :label="$t('challenges.ongoing.add.comment')"
                disabled
              />
              <FieldInput
                v-if="challenge.linkProofUrl"
                role="link"
                :model-value="stripHttpProtocol(challenge.linkProofUrl)"
                :label="$t('challenges.ongoing.add.link')"
                disabled
              />
              <FieldMultiFileUploader
                v-if="getProofFiles(challenge).length"
                :id="`challenge-file-display-${challenge.userChallengeId}`"
                :label="$t('challenges.ongoing.waiting.files')"
                :readonly-files="getProofFiles(challenge)"
                view-only
              />
            </div>
            <BaseButton
              size="lg"
              disabled
              class="!bg-background-warning-default w-full text-foreground-warning-default border-none"
              >{{ $t('challenges.ongoing.waiting.cta') }}</BaseButton
            >
          </div>
        </template>
      </Modal>
    </template>

    <BaseEmptyState
      v-else
      class="flex h-full flex-col items-center justify-center"
      :text="$t('challengesView.empty.all')"
      type="empty-list"
    />
  </div>
</template>
